import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Divider, Tooltip, Typography, styled } from "@mui/material";
import type { MSHatData, ConnectorItem, RegionSalaryData } from "../../types";
import { RegionIcon } from "../right-panel/IconRegion";
import { NationalIcon } from "../right-panel/IconNational";
import { useIsTruncated } from "../../hooks/useIsTruncated";
import { toSrc } from "../../utils/toSrc";

// Icons exported from Figma
import iconPowerskillsImport from "./assets/icon-skill-powerskills.svg";
import iconProskillsImport from "./assets/icon-skill-proskills.svg";
import skillConnectorImport from "./assets/skill-connector.svg";
import iconEndorsedImport from "./assets/icon-endorsed.svg";

const iconPowerskills = toSrc(
  iconPowerskillsImport as string | { src: string },
);
const iconProskills = toSrc(iconProskillsImport as string | { src: string });
const skillConnector = toSrc(skillConnectorImport as string | { src: string });
const iconEndorsed = toSrc(iconEndorsedImport as string | { src: string });

const POWERSKILLS_PROMPT = "What durable skills are most important?";
const PROSKILLS_PROMPT = "What are some important technical skills?";

interface BackLeftPanelProps {
  hat: MSHatData;
  endorsedSkillIds?: string[];
  selectedRegionSalary?: RegionSalaryData;
  nationalSalary?: RegionSalaryData;
}

const PanelContainer = styled(Box, {
  label: "BackLeftPanelContainer",
})(() => ({
  width: 368,
  flexShrink: 0,
  height: 569,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(145, 158, 171, 0.3)",
    borderRadius: "3px",
  },
}));

const HatName = styled(Typography, {
  label: "BackLeftHatName",
})(() => ({
  fontFamily: "Nunito",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: "48px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

const SalaryLabel = styled(Typography, {
  label: "BackLeftSalaryLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "24px",
  color: "#ffd666",
  textAlign: "center",
}));

const SalaryRegionLabel = styled(Typography, {
  label: "BackLeftSalaryRegionLabel",
  shouldForwardProp: (prop) => prop !== "labelColor",
})<{ labelColor: string }>(({ labelColor }) => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "22px",
  color: labelColor,
  whiteSpace: "nowrap",
  // Shrink (and ellipsize) when the region name is too long for its half.
  flex: "0 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const SalaryAmount = styled(Typography, {
  label: "BackLeftSalaryAmount",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffd666",
  whiteSpace: "nowrap",
  flexShrink: 0,
}));

const SkillRow = styled(Box, {
  label: "BackLeftSkillRow",
})(() => ({
  display: "flex",
  gap: 8,
  alignItems: "stretch",
  width: "100%",
}));

const IconColumn = styled(Box, {
  label: "BackLeftIconColumn",
})(() => ({
  width: 64,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const SkillIcon = styled("img", {
  label: "BackLeftSkillIcon",
})(() => ({
  height: 64,
  width: "auto",
  flexShrink: 0,
}));

// Dashed squiggle that stretches to connect this section's icon to the next.
const Connector = styled(Box, {
  label: "BackLeftSkillConnector",
})(() => ({
  flex: 1,
  width: 24,
  minHeight: 48,
  marginTop: 4,
  backgroundImage: `url("${skillConnector}")`,
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
}));

const SkillContent = styled(Box, {
  label: "BackLeftSkillContent",
})(() => ({
  position: "relative",
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 8,
}));

const ChipRow = styled(Box, {
  label: "BackLeftChipRow",
  shouldForwardProp: (prop) => prop !== "equalWidth",
})<{ equalWidth?: boolean }>(({ equalWidth }) => ({
  display: "flex",
  gap: 8,
  width: "100%",
  ...(equalWidth && {
    "& > *": { flex: 1, minWidth: 0 },
  }),
}));

const SectionLabel = styled(Typography, {
  label: "BackLeftSectionLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textTransform: "uppercase",
}));

const SectionPrompt = styled(Typography, {
  label: "BackLeftSectionPrompt",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
}));

const SkillChip = styled(Box, {
  label: "BackLeftSkillChip",
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected?: boolean }>(({ isSelected }) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  height: 32,
  minWidth: 0,
  maxWidth: "calc(50% - 4px)",
  padding: "0 4px",
  borderRadius: 10,
  cursor: "pointer",
  backgroundColor: isSelected ? "#ffffff" : "#0351ab",
  transition: "background-color 150ms ease",
  "&:hover": {
    backgroundColor: isSelected ? "#ffffff" : "#078DEE",
  },
}));

const SkillChipLabel = styled(Typography, {
  label: "BackLeftSkillChipLabel",
  shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected?: boolean }>(({ isSelected }) => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 500,
  fontSize: "13px",
  lineHeight: "18px",
  color: isSelected ? "#1c252e" : "#ffffff",
  padding: "0 8px",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const DetailArrow = styled(Box, {
  label: "BackLeftDetailArrow",
})(() => ({
  width: 0,
  height: 0,
  borderLeft: "6px solid transparent",
  borderRight: "6px solid transparent",
  borderBottom: "8px solid #ffffff",
  // Overlap the box by 1px so the browser's anti-aliasing seam between the
  // white triangle and the white box below isn't rendered as a faint gray line.
  position: "relative",
  marginBottom: -1,
  zIndex: 1,
}));

const DetailBox = styled(Box, {
  label: "BackLeftDetailBox",
})(() => ({
  backgroundColor: "#ffffff",
  borderRadius: 8,
  padding: "8px 16px",
  width: "100%",
}));

const DetailName = styled("span", {
  label: "BackLeftDetailName",
})(() => ({
  display: "block",
  // The "Proxima Nova" italic @font-face spans all weights, so bold + italic
  // resolves to the regular-italic face. Use the dedicated bold family (its own
  // bold woff) and let the italic slant be synthesized.
  fontFamily: '"ProximaNova Bold", "Proxima Nova", sans-serif',
  fontStyle: "italic",
  fontWeight: 700,
  fontSynthesis: "style",
  fontSize: "12px",
  lineHeight: "18px",
  color: "#1c252e",
}));

const DetailDescription = styled("span", {
  label: "BackLeftDetailDescription",
})(() => ({
  display: "block",
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "18px",
  color: "#1c252e",
}));

const EndorsedImg = () => (
  <Tooltip
    title="You've received a shout out!"
    placement="top"
    slotProps={{
      tooltip: {
        sx: {
          backgroundColor: "#FFAB00",
          color: "#1c252e",
          fontFamily: "Proxima Nova, sans-serif",
          fontWeight: 700,
          fontSize: "12px",
          lineHeight: "18px",
          borderRadius: "6px",
          px: "6px",
          py: "3px",
        },
      },
      arrow: {
        sx: { color: "#FFAB00" },
      },
    }}
    arrow
  >
    <img src={iconEndorsed} alt="Endorsed" width={24} height={24} />
  </Tooltip>
);

const parseDescription = (desc: unknown): string => {
  if (!desc) return "";
  if (typeof desc === "string") {
    try {
      const parsed = JSON.parse(desc);
      if (Array.isArray(parsed) && parsed[0]?.children) {
        return parsed[0].children
          .map((child: { text?: string }) => child.text || "")
          .join(" ");
      }
    } catch {
      return desc;
    }
    return desc;
  }
  if (Array.isArray(desc) && desc[0]?.children) {
    return desc[0].children
      .map((child: { text?: string }) => child.text || "")
      .join(" ");
  }
  return "";
};

const formatSalary = (value?: number): string => {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

// Chip label that ellipsizes when too long and shows the full name on hover.
const ChipLabel = ({
  name,
  isSelected,
}: {
  name: string;
  isSelected?: boolean;
}) => {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>();
  const label = (
    <SkillChipLabel ref={ref} isSelected={isSelected}>
      {name}
    </SkillChipLabel>
  );
  if (!isTruncated) return label;
  return (
    <Tooltip title={name} placement="top" enterDelay={400}>
      {label}
    </Tooltip>
  );
};

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

interface SkillSectionProps {
  icon: string;
  hasConnector: boolean;
  label: string;
  prompt: string;
  items: ConnectorItem[];
  equalWidth?: boolean;
  showEndorsed?: boolean;
  endorsedSet: Set<string>;
  selectedId: string | null;
  onToggle: (id: string) => void;
}

const SkillSection = ({
  icon,
  hasConnector,
  label,
  prompt,
  items,
  equalWidth,
  showEndorsed,
  endorsedSet,
  selectedId,
  onToggle,
}: SkillSectionProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [arrowLeft, setArrowLeft] = useState(0);

  const rows = chunk(items, 2);
  const selectedItem = items.find((i) => i.id === selectedId) || null;
  const selectedRowIndex = rows.findIndex((row) =>
    row.some((i) => i.id === selectedId),
  );

  // Point the detail arrow at the horizontal center of the selected chip.
  useLayoutEffect(() => {
    if (!selectedItem) return;
    const chip = chipRefs.current.get(selectedItem.id);
    if (chip) setArrowLeft(chip.offsetLeft + chip.offsetWidth / 2);
  }, [selectedItem, items]);

  return (
    <SkillRow>
      <IconColumn>
        <SkillIcon src={icon} alt="" />
        {hasConnector && <Connector />}
      </IconColumn>
      <SkillContent ref={contentRef}>
        <Box>
          <SectionLabel>{label}</SectionLabel>
          <SectionPrompt>{prompt}</SectionPrompt>
        </Box>
        {rows.map((row, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <ChipRow equalWidth={equalWidth}>
              {row.map((item) => {
                const isSelected = item.id === selectedId;
                const isEndorsed = showEndorsed && endorsedSet.has(item.id);
                return (
                  <SkillChip
                    key={item.id}
                    ref={(el: HTMLElement | null) => {
                      if (el) chipRefs.current.set(item.id, el);
                      else chipRefs.current.delete(item.id);
                    }}
                    isSelected={isSelected}
                    onClick={() => onToggle(item.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onToggle(item.id);
                      }
                    }}
                  >
                    <ChipLabel name={item.name} isSelected={isSelected} />
                    {isEndorsed && <EndorsedImg />}
                  </SkillChip>
                );
              })}
            </ChipRow>
            {rowIndex === selectedRowIndex && selectedItem && (
              <Box sx={{ width: "100%" }}>
                <DetailArrow
                  style={{ marginLeft: Math.max(0, arrowLeft - 6) }}
                />
                <DetailBox>
                  <DetailName>{selectedItem.name}</DetailName>
                  <DetailDescription>
                    {parseDescription(selectedItem.description)}
                  </DetailDescription>
                </DetailBox>
              </Box>
            )}
          </React.Fragment>
        ))}
      </SkillContent>
    </SkillRow>
  );
};

export const BackLeftPanel = ({
  hat,
  endorsedSkillIds = [],
  selectedRegionSalary,
  nationalSalary,
}: BackLeftPanelProps) => {
  const powerSkills = hat.msPowerSkills || [];
  const proSkills = hat.msProSkills || [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const endorsedSet = new Set(endorsedSkillIds);

  const toggle = (id: string) =>
    setSelectedId((prev) => (prev === id ? null : id));

  // The region stat is always labeled "Your Region"; a tooltip reveals which
  // region it actually came from (chosen upstream by an MSA -> state ->
  // national fallback). When the shown value equals the national value — the
  // regional figure defaulted to or matched national — flag it with an
  // asterisk and swap the tooltip to explain that instead.
  const regionMean = selectedRegionSalary?.payRange?.mean;
  const nationalMean = nationalSalary?.payRange?.mean;
  const regionEqualsNational =
    regionMean != null && nationalMean != null && regionMean === nationalMean;
  const regionTooltip = regionEqualsNational
    ? "Regional salary not available – showing National salary"
    : (selectedRegionSalary?.regionName ?? "");

  return (
    <PanelContainer>
      {/* Title + salary */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <HatName>{hat.displayName}</HatName>
        <SalaryLabel>Average Salary</SalaryLabel>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: 1,
          }}
        >
          <Tooltip title={regionTooltip} placement="top" enterDelay={400}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 16,
                  display: "flex",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <RegionIcon />
              </Box>
              <SalaryRegionLabel labelColor="#d3fcd2">
                Your Region
              </SalaryRegionLabel>
              <SalaryAmount>
                {formatSalary(selectedRegionSalary?.payRange?.mean)}
                {regionEqualsNational ? "*" : ""}
              </SalaryAmount>
            </Box>
          </Tooltip>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 18,
                display: "flex",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <NationalIcon />
            </Box>
            <SalaryRegionLabel labelColor="#efd6ff">National</SalaryRegionLabel>
            <SalaryAmount>
              {formatSalary(nationalSalary?.payRange?.mean)}
            </SalaryAmount>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(145, 158, 171, 0.2)" }} />

      {/* Skills */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {powerSkills.length > 0 && (
          <SkillSection
            icon={iconPowerskills}
            hasConnector={proSkills.length > 0}
            label="Powerskills"
            prompt={POWERSKILLS_PROMPT}
            items={powerSkills}
            showEndorsed
            endorsedSet={endorsedSet}
            selectedId={selectedId}
            onToggle={toggle}
          />
        )}

        {proSkills.length > 0 && (
          <SkillSection
            icon={iconProskills}
            hasConnector={false}
            label="Proskills"
            prompt={PROSKILLS_PROMPT}
            items={proSkills}
            equalWidth
            endorsedSet={endorsedSet}
            selectedId={selectedId}
            onToggle={toggle}
          />
        )}
      </Box>
    </PanelContainer>
  );
};
